/**
 * Inspo Feed — Pinterest Inspiration Grid
 * Fetches Pinterest board pins via Cloud API and renders a
 * CSS Grid mosaic with mixed tile sizes (large, small, tall).
 *
 * Features: board filter pills, cascade fade-in animation,
 *           hover overlay with pin title, responsive grid (6→4→3→2 columns).
 */

(function() {
  'use strict';

  var API_BASE = 'https://fz8y24ewil.execute-api.us-east-1.amazonaws.com/staging';
  var ROW_STAGGER = 80;
  var MIN_BOARD_PINS = 3; // Hide boards with fewer pins from filter

  // Mixed-size tile pattern — matches gallery-masonry aesthetic
  var TILE_PATTERN = ['tile-sm', 'tile-sm', 'tile-lg', 'tile-sm', 'tile-tall', 'tile-sm', 'tile-sm', 'tile-sm', 'tile-tall', 'tile-sm', 'tile-lg', 'tile-sm'];

  var gridEl, loadingEl, errorEl, filterBarEl;
  var cascadeObserver = null;
  var allBoards = [];
  var allPins = [];

  // ===== INIT =====

  async function init() {
    var container = document.querySelector('.inspo-feed-section');
    if (!container) return;

    gridEl = container.querySelector('.inspo-feed-grid');
    loadingEl = container.querySelector('.inspo-feed-loading');
    errorEl = container.querySelector('.inspo-feed-error');
    filterBarEl = container.querySelector('#inspo-filter-bar');

    // Set row height to match column width (true-square tiles)
    syncGridRowHeight();
    window.addEventListener('resize', syncGridRowHeight);

    // Set up IntersectionObserver for cascade animation
    setupCascadeObserver();

    try {
      if (loadingEl) loadingEl.style.display = '';

      var res = await fetch(API_BASE + '/api/gallery/pinterest-feed');
      if (!res.ok) throw new Error('HTTP ' + res.status);
      var data = await res.json();

      if (loadingEl) loadingEl.style.display = 'none';

      // Handle both new (boards + allPins) and legacy (pins) response shapes
      if (data.boards && data.boards.length > 0) {
        allBoards = data.boards;
        allPins = data.allPins || [];
        renderFilterPills(allBoards);
        renderPins(allPins);
      } else if (data.pins && data.pins.length > 0) {
        renderPins(data.pins);
      } else {
        if (errorEl) errorEl.style.display = 'block';
      }
    } catch (err) {
      console.error('[InspoFeed] Failed to load:', err);
      if (loadingEl) loadingEl.style.display = 'none';
      if (errorEl) errorEl.style.display = 'block';
    }
  }

  // ===== FILTER PILLS =====

  function renderFilterPills(boards) {
    if (!filterBarEl) return;

    var filterable = boards.filter(function(b) { return b.pinCount >= MIN_BOARD_PINS; });
    if (filterable.length < 2) return; // No point showing filters for 0-1 boards

    // "All" pill
    var allPill = document.createElement('button');
    allPill.className = 'inspo-filter-pill inspo-filter-pill--active';
    allPill.type = 'button';
    allPill.dataset.slug = 'all';
    allPill.textContent = 'All';
    allPill.addEventListener('click', function() { handleFilterClick('all'); });
    filterBarEl.appendChild(allPill);

    // Board pills
    filterable.forEach(function(board) {
      var pill = document.createElement('button');
      pill.className = 'inspo-filter-pill';
      pill.type = 'button';
      pill.dataset.slug = board.slug;
      pill.textContent = board.name;
      pill.addEventListener('click', function() { handleFilterClick(board.slug); });
      filterBarEl.appendChild(pill);
    });
  }

  function handleFilterClick(slug) {
    // Clear grid
    if (gridEl) gridEl.innerHTML = '';

    // Reset cascade observer
    setupCascadeObserver();

    // Render the right pins
    if (slug === 'all') {
      renderPins(allPins);
    } else {
      var board = allBoards.find(function(b) { return b.slug === slug; });
      if (board) renderPins(board.pins);
    }

    // Update active pill
    var pills = filterBarEl ? filterBarEl.querySelectorAll('.inspo-filter-pill') : [];
    for (var i = 0; i < pills.length; i++) {
      if (pills[i].dataset.slug === slug) {
        pills[i].classList.add('inspo-filter-pill--active');
      } else {
        pills[i].classList.remove('inspo-filter-pill--active');
      }
    }
  }

  // Expose for external use (event planner quiz "Browse Inspiration" button)
  window.inspoFeedFilterTo = function(slug) {
    if (!filterBarEl) return;
    var section = document.getElementById('inspo-feed');
    if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(function() { handleFilterClick(slug); }, 400);
  };

  // ===== GRID ROW HEIGHT (keeps tiles square) =====

  function syncGridRowHeight() {
    // Sync both the live grid and the loading skeleton
    var grids = document.querySelectorAll('.inspo-feed-grid, .inspo-feed-loading');
    grids.forEach(function(el) {
      var containerWidth = el.clientWidth;
      if (containerWidth <= 0) return;
      var gap = parseFloat(getComputedStyle(el).columnGap) || parseFloat(getComputedStyle(el).gap) || 12;
      var colParts = getComputedStyle(el).gridTemplateColumns.split(' ').filter(function(v) { return parseFloat(v) > 0; });
      var cols = colParts.length;
      if (cols < 1) {
        var w = window.innerWidth;
        cols = w <= 480 ? 2 : w <= 768 ? 3 : w <= 1024 ? 4 : 6;
      }
      var colWidth = (containerWidth - (cols - 1) * gap) / cols;
      if (colWidth > 0) {
        el.style.gridAutoRows = Math.round(colWidth) + 'px';
      }
    });
  }

  // ===== CASCADE ANIMATION =====

  function setupCascadeObserver() {
    if (!('IntersectionObserver' in window)) {
      cascadeObserver = null;
      return;
    }

    // Disconnect old observer if re-initializing (filter change)
    if (cascadeObserver) cascadeObserver.disconnect();

    cascadeObserver = new IntersectionObserver(function(entries) {
      var toAnimate = [];
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          toAnimate.push(entry.target);
          cascadeObserver.unobserve(entry.target);
        }
      });

      if (toAnimate.length === 0) return;

      toAnimate.sort(function(a, b) {
        return parseInt(a.dataset.tileIndex, 10) - parseInt(b.dataset.tileIndex, 10);
      });

      toAnimate.forEach(function(tile, i) {
        tile.style.transitionDelay = (i * ROW_STAGGER) + 'ms';
        tile.classList.add('inspo-tile--visible');
        setTimeout(function() {
          tile.style.transitionDelay = '0ms';
        }, (i * ROW_STAGGER) + 600);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px 50px 0px' });
  }

  // ===== RENDER =====

  function renderPins(pins) {
    if (!gridEl) return;

    pins.forEach(function(pin, i) {
      var sizeClass = TILE_PATTERN[i % TILE_PATTERN.length];
      var tile = document.createElement('a');
      tile.className = 'inspo-tile ' + sizeClass;
      tile.href = pin.link;
      tile.target = '_blank';
      tile.rel = 'noopener';
      tile.setAttribute('data-tile-index', String(i));
      tile.style.backgroundColor = pin.dominantColor || '#eee';

      var titleText = escapeHtml(pin.title || '');

      tile.innerHTML =
        '<div class="inspo-tile-image">' +
          '<img src="' + escapeHtml(pin.imageUrl) + '" alt="' + titleText + '" loading="lazy">' +
        '</div>' +
        (titleText ?
          '<div class="inspo-tile-overlay">' +
            '<span class="inspo-tile-title">' + titleText + '</span>' +
          '</div>' : '');

      gridEl.appendChild(tile);

      if (cascadeObserver) {
        cascadeObserver.observe(tile);
      } else {
        tile.classList.add('inspo-tile--visible');
      }
    });

    // Re-sync row height after content is rendered
    syncGridRowHeight();
  }

  // ===== UTILITIES =====

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // ===== BOOT =====

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
